// **React Imports
import { memo, useCallback, useEffect, useState } from 'react'

// **MUI Imports
import { Box, Button, InputAdornment, Typography } from '@mui/material'

// **Component Imports
import ControlledTextField from '../controlled-textfield'
import CustomDialog from '../dialog'
import Icon from '../icon'
import OverlayLoading from '../overlay-loading'

// **Third party Imports
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

// **Redux Imports
import { useDispatch, useSelector } from 'react-redux'

// **Utils Imports
import { checkCandidateAccess, showErrorToast } from '../../utils/utils'
import { candidateDetailsValidations } from '../../utils/validations'

// **API Imports
import {
  getCandidateProfile,
  uploadCandidateProfileDetails
} from '../../axios/api/candidate'
import { validateCandidateProfile } from '../../axios/api/profile'
import FieldsetContainer from '../../pages/contest/edit/components/FieldsetContainer'
import { setProfileDetails } from '../../store/slice/auth'

const defaultValues = {
  email: '',
  name: '',
  phoneNumber: ''
}

const CandidateDetailsModal = () => {
  const dispatch = useDispatch()
  
  // redux
  const isLoggedIn = useSelector((state) => state.auth.token)
  const userData = useSelector((state) => state.auth.profile)

  // state
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  // form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(candidateDetailsValidations)
  })

  const fetchProfile = useCallback(async () => {
    setLoading(true)
    const response = await getCandidateProfile()
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      dispatch(setProfileDetails(response))
    }
  }, [dispatch])

  const updateDetails = useCallback(
    async (payload) => {
      setLoading(true)
      const response = await uploadCandidateProfileDetails(payload)
      setLoading(false)

      if (response?.isError) {
        showErrorToast(response)
      } else {
        toast.success('Profile updated successfully')
        fetchProfile()
        setOpen(false)
      }
    },
    [fetchProfile]
  )

  // Submit Handler
  const onSubmit = useCallback(
    async (data) => {
      if (data) await updateDetails(data)
    },
    [updateDetails]
  )

  // API's
  const fetchCandidateLoginFlag = useCallback(async () => {
    setLoading(true)
    const response = await validateCandidateProfile()
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      setOpen(response)
    }
  }, [])

  useEffect(() => {
    if (isLoggedIn && checkCandidateAccess()) {
      fetchCandidateLoginFlag()
    }
  }, [isLoggedIn, fetchCandidateLoginFlag])

  useEffect(() => {
    if (!userData) return
    const { name = '', email = '', phoneNumber = '' } = userData
    setValue('name', name)
    setValue('email', email)
    setValue('phoneNumber', phoneNumber)
  }, [userData, setValue])

  useEffect(() => {
    return () => {
      setOpen(false)
    }
  }, [])

  return (
    <Box>
      {loading && <OverlayLoading />}

      <CustomDialog
        outsideClikClose={false}
        open={open}
        dialogPaperStyle={{
          width: '90%',
          maxWidth: '400px',
          '& fieldset': {
            backgroundColor: 'var(--grey-50)'
          },
          '& .MuiInputBase-root': {
            backgroundColor: 'var(--common-white) !important'
          }
        }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="candidate-details-modal-container"
        >
          <Typography variant="h3" sx={{ mb: 1 }}>
            Complete Your Profile
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 8 }}>
            Just a few quick details to get you started. 🚀
          </Typography>

          <FieldsetContainer label="Profile Details">
            <Box mb={2} display="flex" flexDirection="column" gap="16px">
              <ControlledTextField
                autoFocus
                fullWidth
                required
                name="name"
                control={control}
                label="Full name"
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Icon icon="mdi:person" />
                    </InputAdornment>
                  )
                }}
              />

              <ControlledTextField
                fullWidth
                required
                name="email"
                disabled
                control={control}
                label="Email"
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Icon icon="mdi:email-outline" />
                    </InputAdornment>
                  )
                }}
              />

              <ControlledTextField
                fullWidth
                name="phoneNumber"
                control={control}
                label="Phone Number"
                error={Boolean(errors.phoneNumber)}
                helperText={errors.phoneNumber?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Icon icon="mdi:phone" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </FieldsetContainer>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}
          >
            <Button variant="contained" color="primary" type="submit">
              Save
            </Button>
          </Box>
        </form>
      </CustomDialog>
    </Box>
  )
}

export default memo(CandidateDetailsModal)
